import React, { useState } from 'react';
import type { HeartSettings } from '../../lib/types';
import { Countdown } from '../Countdown';

interface SettingsSectionProps {
  initialSettings: HeartSettings;
  onSave: (settings: HeartSettings) => Promise<void>;
  onPreviewWithSettings: (settings: HeartSettings) => void;
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({
  initialSettings,
  onSave,
  onPreviewWithSettings,
}) => {
  const [formData, setFormData] = useState<HeartSettings>({ ...initialSettings });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleLoveRatioChange = (val: number) => {
    const love = Math.max(10, Math.min(90, val));
    setFormData(prev => ({
      ...prev,
      love_ratio: love,
      nickname_ratio: 100 - love,
    }));
    setSaveSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg(null);
    setSaveSuccess(false);

    if (formData.min_font_size > formData.max_font_size) {
      setErrorMsg('Minimum font size cannot exceed maximum font size');
      setIsSaving(false);
      return;
    }

    try {
      await onSave(formData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-section settings-section">
      <div className="section-header">
        <h2>Keepsake & Visual Configuration</h2>
        <p className="section-description">
          Fine-tune the mathematical composition, content distribution, red color palette, and security behaviors.
        </p>
      </div>

      {saveSuccess && (
        <div className="notice-banner banner-success">
          Settings successfully saved and persisted to database.
        </div>
      )}

      {errorMsg && <div className="notice-banner banner-danger">{errorMsg}</div>}

      <form onSubmit={handleSubmit} className="settings-form">
        {/* Content Ratio Card */}
        <div className="settings-card">
          <h3>Content Distribution</h3>
          <p className="card-hint">
            Specify the proportion of phrases selected from multilingual love declarations vs. personal nicknames.
          </p>

          <div className="ratio-slider-container">
            <div className="ratio-display-bar">
              <div
                className="ratio-bar-love"
                style={{ width: `${formData.love_ratio}%` }}
                title={`Love phrases: ${formData.love_ratio}%`}
              >
                <span>Love {formData.love_ratio}%</span>
              </div>
              <div
                className="ratio-bar-nick"
                style={{ width: `${formData.nickname_ratio}%` }}
                title={`Nicknames: ${formData.nickname_ratio}%`}
              >
                <span>Nicknames {formData.nickname_ratio}%</span>
              </div>
            </div>

            <div className="slider-control">
              <input
                type="range"
                min="10"
                max="90"
                step="5"
                value={formData.love_ratio}
                onChange={e => handleLoveRatioChange(Number(e.target.value))}
                className="range-input"
              />
              <div className="slider-labels">
                <span>10% Love / 90% Nicknames</span>
                <span>Default: 70% / 30%</span>
                <span>90% Love / 10% Nicknames</span>
              </div>
            </div>
          </div>
        </div>

        {/* Typography & Spacing */}
        <div className="settings-card">
          <h3>Typography & Density</h3>
          <p className="card-hint">
            Adjust responsive font boundaries and row spacing across device viewports.
          </p>

          <div className="form-grid-2">
            <div className="form-field">
              <label htmlFor="min-font">Minimum Font Size ({formData.min_font_size}px)</label>
              <input
                id="min-font"
                type="range"
                min="8"
                max="20"
                value={formData.min_font_size}
                onChange={e =>
                  setFormData({ ...formData, min_font_size: Number(e.target.value) })
                }
                className="range-input"
              />
            </div>

            <div className="form-field">
              <label htmlFor="max-font">Maximum Font Size ({formData.max_font_size}px)</label>
              <input
                id="max-font"
                type="range"
                min="14"
                max="40"
                value={formData.max_font_size}
                onChange={e =>
                  setFormData({ ...formData, max_font_size: Number(e.target.value) })
                }
                className="range-input"
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-field">
              <label htmlFor="text-density">Composition Density</label>
              <select
                id="text-density"
                value={formData.text_density}
                onChange={e =>
                  setFormData({
                    ...formData,
                    text_density: e.target.value as 'compact' | 'balanced' | 'spacious',
                  })
                }
                className="select-field"
              >
                <option value="compact">Compact (Tighter rows, higher phrase count)</option>
                <option value="balanced">Balanced (Optimal readability & silhouette)</option>
                <option value="spacious">Spacious (Airy spacing, fewer phrases)</option>
              </select>
            </div>

            <div className="form-field">
              <label htmlFor="animation-style">Entrance Transition</label>
              <select
                id="animation-style"
                value={formData.animation_style}
                onChange={e =>
                  setFormData({
                    ...formData,
                    animation_style: e.target.value as 'none' | 'subtle' | 'gentle',
                  })
                }
                className="select-field"
              >
                <option value="subtle">Subtle (Restrained soft fade-in)</option>
                <option value="gentle">Gentle (Slow peaceful emergence)</option>
                <option value="none">None (Instant display, zero transition)</option>
              </select>
            </div>
          </div>
        </div>

        {/* Color Palette & Highlights */}
        <div className="settings-card">
          <h3>Red Palette & Accents</h3>
          <p className="card-hint">
            All text uses shades of red on pure black. Configure highlight accents and palette tones.
          </p>

          <div className="palette-preview-row">
            {formData.color_palette.map((color, idx) => (
              <div key={idx} className="palette-chip" style={{ backgroundColor: color }}>
                <span className="chip-code">{color}</span>
              </div>
            ))}
          </div>

          <div className="form-field">
            <label htmlFor="highlight-freq">
              Bright Highlight Frequency ({Math.round(formData.highlight_frequency * 100)}%)
            </label>
            <input
              id="highlight-freq"
              type="range"
              min="0"
              max="0.5"
              step="0.05"
              value={formData.highlight_frequency}
              onChange={e =>
                setFormData({ ...formData, highlight_frequency: Number(e.target.value) })
              }
              className="range-input"
            />
            <span className="field-hint">
              Controls the ratio of phrases rendered in bright vivid crimson versus deep burgundy wine reds.
            </span>
          </div>
        </div>

        {/* Countdown Timer Configuration */}
        <div className="settings-card">
          <h3>Countdown Timer</h3>
          <p className="card-hint">
            Configure a countdown clock towards a designated milestone date and time, and choose where it should be visible.
          </p>

          <div className="form-grid-2">
            <div className="form-field">
              <label htmlFor="countdown-visibility">Countdown Visibility</label>
              <select
                id="countdown-visibility"
                value={formData.countdown_visibility ?? 'none'}
                onChange={e =>
                  setFormData({
                    ...formData,
                    countdown_visibility: e.target.value as 'none' | 'login' | 'heart' | 'both',
                  })
                }
                className="select-field"
              >
                <option value="none">Don&apos;t show at all (Hidden)</option>
                <option value="login">Shown on login page</option>
                <option value="heart">Shown under the heart</option>
                <option value="both">Shown on both login page & under the heart</option>
              </select>
              <span className="field-hint">
                Controls where the visitor or viewer sees the countdown.
              </span>
            </div>

            <div className="form-field">
              <label htmlFor="countdown-target">Target Date & Time</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  id="countdown-target"
                  type="datetime-local"
                  value={formData.countdown_target ? formData.countdown_target.slice(0, 16) : ''}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      countdown_target: e.target.value || null,
                    })
                  }
                  className="input-field"
                  style={{ flex: 1 }}
                />
                {formData.countdown_target && (
                  <button
                    type="button"
                    className="action-btn action-btn-secondary"
                    onClick={() =>
                      setFormData({
                        ...formData,
                        countdown_target: null,
                      })
                    }
                    style={{ padding: '6px 10px', fontSize: '11px', whiteSpace: 'nowrap' }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <span className="field-hint">
                Select target date and time. Leave empty to disable.
              </span>
            </div>
          </div>

          <div className="form-field" style={{ marginTop: 'var(--space-md)' }}>
            <label htmlFor="countdown-title">Title / Occasion Label (Optional)</label>
            <input
              id="countdown-title"
              type="text"
              maxLength={100}
              placeholder="e.g. Until We Meet, Our Special Day, etc."
              value={formData.countdown_title ?? ''}
              onChange={e =>
                setFormData({
                  ...formData,
                  countdown_title: e.target.value,
                })
              }
              className="input-field"
            />
            <span className="field-hint">
              Heading shown above the countdown digits.
            </span>
          </div>

          {formData.countdown_target && (
            <div style={{ marginTop: 'var(--space-md)' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '6px' }}>
                Live Admin Preview ({formData.countdown_visibility === 'none' ? 'Currently Hidden' : `Visibility: ${formData.countdown_visibility}`})
              </div>
              <Countdown
                target={formData.countdown_target}
                title={formData.countdown_title}
                variant="admin"
              />
            </div>
          )}
        </div>

        {/* Chat History & Visibility */}
        <div className="settings-card">
          <h3>Chat History Visibility</h3>
          <p className="card-hint">
            Control the display of the &quot;Chat history&quot; button across the keepsake.
          </p>

          <div className="form-field" style={{ maxWidth: '420px' }}>
            <label htmlFor="chat-visibility">Chat Button Visibility</label>
            <select
              id="chat-visibility"
              value={formData.chat_history_visibility ?? 'authenticated'}
              onChange={e =>
                setFormData({
                  ...formData,
                  chat_history_visibility: e.target.value as 'authenticated' | 'admin_only' | 'hidden' | 'public',
                })
              }
              className="select-field"
            >
              <option value="authenticated">Shown for Authenticated Viewers (Default)</option>
              <option value="admin_only">Shown for Admin Only</option>
              <option value="hidden">Don&apos;t show at all (Hidden)</option>
              <option value="public">Shown for Everyone (Public)</option>
            </select>
            <span className="field-hint">
              When hidden, the chat history button will not appear in the viewer controls.
            </span>
          </div>
        </div>

        {/* Gemini AI Configuration */}
        <div className="settings-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <h3>Gemini AI Intelligence & Dynamic Analysis</h3>
            <span
              style={{
                fontSize: '11px',
                padding: '2px 8px',
                borderRadius: '10px',
                background: formData.gemini_api_key ? 'rgba(37, 211, 102, 0.15)' : 'rgba(255, 77, 106, 0.15)',
                color: formData.gemini_api_key ? '#25d366' : 'var(--red-7)',
                border: `1px solid ${formData.gemini_api_key ? '#25d366' : 'var(--red-5)'}`,
              }}
            >
              {formData.gemini_api_key ? '✓ API Key Configured' : 'No Key Set'}
            </span>
          </div>
          <p className="card-hint">
            Enable live, dynamic relationship analysis and conversational AI Q&amp;A using Google Gemini. The key is securely stored in your Cloudflare D1 database and never exposed to client browsers.
          </p>

          <div className="form-grid-2">
            <div className="form-field">
              <label htmlFor="gemini-key">Gemini API Key</label>
              <input
                id="gemini-key"
                type="password"
                placeholder="AIzaSy..."
                value={formData.gemini_api_key ?? ''}
                onChange={e =>
                  setFormData({
                    ...formData,
                    gemini_api_key: e.target.value,
                  })
                }
                className="input-field"
                autoComplete="off"
              />
              <span className="field-hint">
                Obtain a free API key at <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--red-7)' }}>Google AI Studio</a>.
              </span>
            </div>

            <div className="form-field">
              <label htmlFor="ai-model">Gemini Model</label>
              <select
                id="ai-model"
                value={formData.ai_model ?? 'gemini-1.5-flash'}
                onChange={e =>
                  setFormData({
                    ...formData,
                    ai_model: e.target.value,
                  })
                }
                className="select-field"
              >
                <option value="gemini-1.5-flash">Gemini 1.5 Flash (Recommended: Fast, Free Tier Generous)</option>
                <option value="gemini-2.0-flash">Gemini 2.0 Flash (Next-Gen Experimental)</option>
                <option value="gemini-1.5-pro">Gemini 1.5 Pro (Deep Psychological Reasoning)</option>
              </select>
              <span className="field-hint">Select model version for live queries and evolving synthesis.</span>
            </div>
          </div>
        </div>

        {/* Dynamic Chat Data Ingestion */}
        <div className="settings-card">
          <h3>Chat Archive Ingestion & Live Sync</h3>
          <p className="card-hint">
            Import conversations from WhatsApp (<code>wp.txt</code>) and Instagram DMs (<code>insta1.json</code>, <code>insta2.json</code>, etc.). Supports all 33,186+ records with automatic character encoding decoding.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div
              style={{
                border: '1px dashed rgba(224, 49, 74, 0.4)',
                borderRadius: '4px',
                padding: '18px',
                textAlign: 'center',
                background: 'rgba(20, 10, 14, 0.4)',
              }}
            >
              <label htmlFor="chat-archive-upload" style={{ cursor: 'pointer', display: 'block' }}>
                <div style={{ fontSize: '24px', marginBottom: '6px' }}>📂</div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Upload wp.txt or instaX.json
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Select WhatsApp export (.txt) or Instagram direct messages (.json)
                </div>
              </label>
              <input
                id="chat-archive-upload"
                type="file"
                accept=".txt,.json"
                style={{ display: 'none' }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;

                  try {
                    const text = await file.text();
                    let count = 0;
                    if (file.name.endsWith('.json')) {
                      const { parseInstagramJson } = await import('../../lib/chatParsers');
                      const msgs = parseInstagramJson(text);
                      count = msgs.length;
                      alert(`Successfully parsed ${count.toLocaleString()} Instagram messages from ${file.name}.`);
                    } else {
                      const { parseWhatsAppText } = await import('../../lib/chatParsers');
                      const msgs = parseWhatsAppText(text);
                      count = msgs.length;
                      alert(`Successfully parsed ${count.toLocaleString()} WhatsApp messages from ${file.name}.`);
                    }
                  } catch (err: unknown) {
                    alert(`Failed to parse ${file.name}: ${err instanceof Error ? err.message : String(err)}`);
                  }
                }}
              />
            </div>

            {/* Purge All Chat Messages Danger Zone */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                background: 'rgba(255, 77, 106, 0.08)',
                border: '1px solid rgba(255, 77, 106, 0.25)',
                borderRadius: '4px',
                marginTop: '8px',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div>
                <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--red-7)' }}>
                  Purge All Stored Chat Messages
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Permanently wipe all imported WhatsApp and Instagram records from Cloudflare D1 database.
                </div>
              </div>
              <button
                type="button"
                className="action-btn action-btn-danger"
                style={{ fontSize: '11px', padding: '6px 14px' }}
                onClick={async () => {
                  if (!confirm('Are you completely sure you want to delete all chat records from the database? This cannot be undone.')) {
                    return;
                  }
                  try {
                    const res = await fetch('/api/admin/chat-clear', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                      },
                    });
                    const d = (await res.json()) as { success?: boolean; message?: string; error?: string };
                    if (d.success) {
                      alert(d.message || 'All chat messages cleared successfully.');
                    } else {
                      alert(d.error || 'Failed to clear chat messages.');
                    }
                  } catch (err: unknown) {
                    alert(err instanceof Error ? err.message : 'Error clearing chat messages.');
                  }
                }}
              >
                Delete All Chat Messages
              </button>
            </div>
          </div>
        </div>

        {/* Security & Viewer Permissions */}
        <div className="settings-card">
          <h3>Security & Viewer Permissions</h3>

          <div className="form-grid-2">
            <div className="form-field">
              <label htmlFor="session-dur">Session Lifetime (Hours)</label>
              <input
                id="session-dur"
                type="number"
                min="1"
                max="720"
                value={formData.session_duration_hours ?? 24}
                onChange={e =>
                  setFormData({
                    ...formData,
                    session_duration_hours: Math.max(1, Number(e.target.value)),
                  })
                }
                className="input-field"
              />
              <span className="field-hint">Default 24 hours (up to 30 days = 720h).</span>
            </div>

            <div className="checkboxes-stack">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.allow_viewer_rearrange}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      allow_viewer_rearrange: e.target.checked,
                    })
                  }
                />
                <span>Allow viewer to generate new arrangements</span>
              </label>

              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.strict_reauth ?? false}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      strict_reauth: e.target.checked,
                    })
                  }
                />
                <span>Strict reauthentication (require login on browser refresh)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="settings-form-actions">
          <button
            type="button"
            className="action-btn action-btn-preview"
            onClick={() => onPreviewWithSettings(formData)}
          >
            Preview Unsaved Settings
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="action-btn action-btn-primary"
          >
            {isSaving ? 'Saving Configuration...' : 'Save Settings to Database'}
          </button>
        </div>
      </form>
    </div>
  );
};
